import type { DictionaryPhrases } from '@/lib/dictPlugin';

/**
 * What a render gets: `fresh` is the requested timestamp, `stale` an older one served instead,
 * `missing` nothing at all. Only `fresh` makes a render cacheable.
 */
export type SsrDictionaryStatus = 'fresh' | 'stale' | 'missing';

export interface SsrDictionaryResult {
  translations: DictionaryPhrases;
  status: SsrDictionaryStatus;
}

export interface SsrDictionaryKey {
  baseUrl: string;
  lang: string;
  id: string;
  timestamp: string;
}

export interface SsrDictionaryCacheOptions {
  /** Dictionary fetches one renderer keeps open at once, across every site and language. */
  maxConcurrent?: number;
  /** How long a failed timestamp is not fetched again. */
  failureCooldownMs?: number;
  now?: () => number;
}

/** CD pods can report different timestamps at once, so more than the newest one stays fresh. */
const VERSIONS_KEPT = 2;

/**
 * Bounds what one SSR process asks of the dictionary endpoint. During the 2026-10-30 incident Sitecore
 * CD changed the dictionary timestamp ~40 times in half an hour while failing, and every render
 * fetched anew - unbounded, uncached, against the failing CD.
 *
 * Renders needing the same timestamp share one fetch. A render that would wait on a saturated
 * endpoint - its timestamp already in flight, the concurrency cap reached, a recent failure, or the
 * fetch failing - gets the last good translations instead, marked `stale` so it is neither cached
 * nor hydrated.
 */
export const createSsrDictionaryCache = ({
  maxConcurrent = 3,
  failureCooldownMs = 10_000,
  now = Date.now,
}: SsrDictionaryCacheOptions = {}) => {
  /** Per dictionary, its fetched timestamps - the most recently fetched last. */
  const versions = new Map<string, Map<string, DictionaryPhrases>>();
  const inFlight = new Map<string, Promise<DictionaryPhrases>>();
  const failedAt = new Map<string, number>();

  const fallback = (dictionaryKey: string): SsrDictionaryResult => {
    const fetched = [...(versions.get(dictionaryKey)?.values() ?? [])];
    const last = fetched[fetched.length - 1];
    return last ? { translations: last, status: 'stale' } : { translations: {}, status: 'missing' };
  };

  const load = async (
    key: SsrDictionaryKey,
    fetchTranslations: () => Promise<DictionaryPhrases>,
    onError: (error: unknown) => void,
  ): Promise<SsrDictionaryResult> => {
    const dictionaryKey = `${key.baseUrl}|${key.lang}|${key.id}`;
    const versionKey = `${dictionaryKey}|${key.timestamp}`;

    const known = versions.get(dictionaryKey);
    const cached = known?.get(key.timestamp);
    if (cached) {
      return { translations: cached, status: 'fresh' };
    }

    const running = inFlight.get(versionKey);
    if (running) {
      if (known?.size) {
        return fallback(dictionaryKey);
      }
      // Nothing to serve instead, so share the fetch. The render that started it reports the error.
      return running.then(
        (translations) => ({ translations, status: 'fresh' as const }),
        () => fallback(dictionaryKey),
      );
    }

    if (now() - (failedAt.get(versionKey) ?? -Infinity) < failureCooldownMs) {
      return fallback(dictionaryKey);
    }
    if (inFlight.size >= maxConcurrent) {
      return fallback(dictionaryKey);
    }

    const request = fetchTranslations().then((translations) => {
      // An empty response would hold until the timestamp changes, so it counts as a failure.
      if (!Object.keys(translations).length) {
        throw new Error('Empty dictionary');
      }
      return translations;
    });
    inFlight.set(versionKey, request);
    try {
      const translations = await request;
      const kept = versions.get(dictionaryKey) ?? new Map<string, DictionaryPhrases>();
      kept.delete(key.timestamp);
      kept.set(key.timestamp, translations);
      while (kept.size > VERSIONS_KEPT) {
        kept.delete(kept.keys().next().value as string);
      }
      versions.set(dictionaryKey, kept);
      failedAt.delete(versionKey);
      return { translations, status: 'fresh' };
    } catch (error) {
      const failedNow = now();
      failedAt.forEach((at, version) => {
        if (failedNow - at >= failureCooldownMs) {
          failedAt.delete(version);
        }
      });
      failedAt.set(versionKey, failedNow);
      onError(error);
      return fallback(dictionaryKey);
    } finally {
      inFlight.delete(versionKey);
    }
  };

  return { load };
};
